import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import DoctorLayout from "../../Layout/DoctorLayout";
import API from "../../Api/Api";
import ENDPOINTS from "../../Api/Endpoints";
import { tokenManager } from "../../Api/tokenManager";
import { showAlert, showConfirm } from "../../feedback/feedbackApi";
import {
  DAYS,
  DOCTOR_SLOT_INTERVAL_CHOICES,
  buildTimeOptionsForInterval,
  clampDoctorSlotIntervalMinutes,
  formatDisplayTime,
  migrateSingleClinicCells,
  minutesToHHMM,
  normalizeToSlotInterval,
  slotBlocksToDoctorRows,
} from "../../utils/providerSlotUtils";
import "./DoctorAvailability.scss";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const normalizeMe = (raw) => raw?.data?.data || raw?.data || raw?.doctor || raw;

const SLOT_PEEK_HOVER_MS = 280;
const SLOT_PEEK_HIDE_MS = 120;
const SLOT_PEEK_HOLD_MS = 450;

const toMinutes = (t) => {
  const [h, m] = String(t || "").split(":").map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return NaN;
  return h * 60 + m;
};

const slugClinicId = (name) => {
  const s = String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
  return s || "clinic";
};

const newClinicId = () => `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
const normalizeClinicName = (value) => String(value || "").trim().toLowerCase();

const MAX_PLACE_PHOTOS = 8;

const photoUrlsFromRow = (r) => {
  const single = String(r?.placePhotoUrl || "").trim();
  const arr = Array.isArray(r?.placePhotoUrls) ? r.placePhotoUrls.map((x) => String(x || "").trim()).filter(Boolean) : [];
  const out = [];
  const seen = new Set();
  for (const u of [...arr, ...(single && /^https:\/\//i.test(single) ? [single] : [])]) {
    if (!u || seen.has(u)) continue;
    seen.add(u);
    out.push(u);
    if (out.length >= MAX_PLACE_PHOTOS) break;
  }
  return out;
};

const PLACE_PHOTO_DISPLAY_MAX = 480;

/** Build legacy Place Photo URLs (stable photo_reference + browser Maps key; avoids expiring JS PhotoService URLs). */
function placePhotoUrlsFromReferences(refs, mapsKey) {
  if (!mapsKey || !Array.isArray(refs)) return [];
  const out = [];
  const seen = new Set();
  for (const ref of refs) {
    const r = String(ref || "").trim();
    if (!r || seen.has(r)) continue;
    seen.add(r);
    out.push(
      `https://maps.googleapis.com/maps/api/place/photo?maxwidth=${PLACE_PHOTO_DISPLAY_MAX}&photo_reference=${encodeURIComponent(r)}&key=${encodeURIComponent(mapsKey)}`
    );
    if (out.length >= MAX_PLACE_PHOTOS) break;
  }
  return out;
}

async function mergeStablePlacePhotosIntoClinicMap(cellMap, mapsKey, requestRefs) {
  if (!mapsKey || !(cellMap instanceof Map) || cellMap.size === 0) return { map: cellMap, changed: false };
  const placeIds = [...new Set([...cellMap.values()].map((c) => String(c.googlePlaceId || "").trim()).filter(Boolean))];
  if (!placeIds.length) return { map: cellMap, changed: false };
  let byPlaceId = {};
  try {
    const r = await requestRefs(placeIds);
    byPlaceId = r?.data?.byPlaceId || {};
  } catch {
    return { map: cellMap, changed: false };
  }
  const next = new Map(cellMap);
  let changed = false;
  for (const [id, c] of cellMap.entries()) {
    const pid = String(c.googlePlaceId || "").trim();
    if (!pid) continue;
    const refs = byPlaceId[pid];
    if (!Array.isArray(refs) || !refs.length) continue;
    const urls = placePhotoUrlsFromReferences(refs, mapsKey);
    if (!urls.length) continue;
    const prevJson = JSON.stringify(c.placePhotoUrls || []);
    const nextJson = JSON.stringify(urls);
    if (prevJson === nextJson) continue;
    changed = true;
    next.set(id, { ...c, placePhotoUrls: urls, placePhotoUrl: urls[0] || "" });
  }
  return { map: next, changed };
}

/** Copy `prev` and insert a new clinic row. */
const insertClinicIntoMapCopy = (prev, payload) => {
  const name = String(payload?.name || "").trim();
  if (!name) return { ok: false, reason: "name" };
  if ([...prev.values()].some((v) => normalizeClinicName(v.name) === normalizeClinicName(name))) {
    return { ok: false, reason: "duplicate" };
  }
  const id = newClinicId();
  const next = new Map(prev);
  next.set(id, {
    name,
    cells: new Set(),
    slotIntervalMinutes: clampDoctorSlotIntervalMinutes(payload?.slotIntervalMinutes ?? 30),
    address: String(payload?.address || "").trim(),
    city: String(payload?.city || "").trim(),
    state: String(payload?.state || "").trim(),
    pincode: String(payload?.pincode || "").trim(),
    latitude: Number.isFinite(Number(payload?.latitude)) ? Number(payload.latitude) : null,
    longitude: Number.isFinite(Number(payload?.longitude)) ? Number(payload.longitude) : null,
    googlePlaceId: String(payload?.googlePlaceId || "").trim(),
    phoneNumber: String(payload?.phoneNumber || "").trim(),
    placePhotoUrls: photoUrlsFromRow(payload),
    placePhotoUrl: photoUrlsFromRow(payload)[0] || "",
  });
  return { ok: true, next, id };
};

/** Add consecutive availability cells on a map copy (for immediate persist). */
const applySlotsToClinicMapCopy = (prev, clinicId, clinicName, day, startTime, spanCount, getBookedDetail, fallbackIntervalMinutes = 30) => {
  const fb = clampDoctorSlotIntervalMinutes(fallbackIntervalMinutes);
  const next = new Map(prev);
  const cur = next.get(clinicId) || {
    name: clinicName || "Clinic",
    cells: new Set(),
    slotIntervalMinutes: fb,
    address: "",
    city: "",
    state: "",
    pincode: "",
    latitude: null,
    longitude: null,
    googlePlaceId: "",
    phoneNumber: "",
    placePhotoUrl: "",
    placePhotoUrls: [],
  };
  const step = clampDoctorSlotIntervalMinutes(cur.slotIntervalMinutes ?? fb);
  const slotTimeOptions = buildTimeOptionsForInterval(step);
  const startIdx = slotTimeOptions.indexOf(startTime);
  if (startIdx < 0) return prev;
  const count = Math.max(1, Math.min(12, Number(spanCount) || 1));
  const set = new Set(cur.cells || []);
  for (let i = 0; i < count; i += 1) {
    const t = slotTimeOptions[startIdx + i];
    if (!t) break;
    if (!getBookedDetail(day, t)) set.add(`${day}|${t}`);
  }
  next.set(clinicId, { ...cur, name: clinicName || cur.name, cells: set });
  return next;
};

const mergePhotoUrls = (existing, incoming) => {
  const seen = new Set();
  const out = [];
  for (const list of [existing, incoming]) {
    if (!Array.isArray(list)) continue;
    for (const u of list) {
      const s = String(u || "").trim();
      if (!s || !/^https:\/\//i.test(s) || seen.has(s)) continue;
      seen.add(s);
      out.push(s);
      if (out.length >= MAX_PLACE_PHOTOS) return out;
    }
  }
  return out;
};

const parseDisplayTimeSlot = (timeSlot) => {
  const raw = String(timeSlot || "").trim();
  if (!raw) return { timePart: "", clinicLabel: "" };
  const parts = raw.split("·").map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 2) return { timePart: parts[0], clinicLabel: parts.slice(1).join(" · ").trim() };
  return { timePart: raw, clinicLabel: "" };
};

const patientIdFromAppointment = (a) => {
  const pid = a?.patientId;
  if (pid && typeof pid === "object" && pid._id) return String(pid._id);
  if (pid) return String(pid);
  return "";
};

const buildSlotSpanLabel = (startTime, slotCount, slotIntervalMinutes) => {
  const count = Math.max(1, Number(slotCount) || 1);
  const step = clampDoctorSlotIntervalMinutes(slotIntervalMinutes);
  const startMins = toMinutes(startTime);
  if (!Number.isFinite(startMins)) return `${count} slot${count > 1 ? "s" : ""} (${count * step} min)`;
  const endMins = startMins + count * step;
  const wrapsNextDay = endMins >= 24 * 60;
  const endTime = minutesToHHMM(endMins % (24 * 60));
  return `${count} slot${count > 1 ? "s" : ""} (${count * step} min) · ${formatDisplayTime(startTime)} - ${formatDisplayTime(endTime)}${wrapsNextDay ? " (+1 day)" : ""}`;
};

const normalizeAppointmentTime = (value, stepMinutes) => {
  const step = clampDoctorSlotIntervalMinutes(stepMinutes);
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^\d{2}:\d{2}$/.test(raw)) return normalizeToSlotInterval(raw, step);
  const display = parseDisplayTimeSlot(raw).timePart || raw;
  /** Slots may be stored as "9:15 AM at Clinic" or "9:15 AM · Clinic" — grab the first clock token. */
  const clockMatch = display.match(/\b(\d{1,2}:\d{2}\s*(?:AM|PM))\b/i);
  const clock = clockMatch ? clockMatch[1].trim() : display.trim();
  const m = clock.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!m) return "";
  let hour = Number(m[1]);
  const minute = Number(m[2]);
  const suffix = m[3].toUpperCase();
  if (suffix === "AM" && hour === 12) hour = 0;
  if (suffix === "PM" && hour < 12) hour += 12;
  return normalizeToSlotInterval(`${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`, step);
};

const clinicSchedulesToRows = (schedules, defaultStepMinutes = 30) => {
  const def = clampDoctorSlotIntervalMinutes(defaultStepMinutes);
  const out = [];
  (Array.isArray(schedules) ? schedules : []).forEach((c) => {
    const clinicName = String(c.clinicName || c.location || "").trim();
    if (!clinicName) return;
    const clinicId = String(c.clinicId || "").trim() || slugClinicId(clinicName);
    const rowStep = clampDoctorSlotIntervalMinutes(c.slotIntervalMinutes ?? def);
    (Array.isArray(c.slots) ? c.slots : []).forEach((s) => {
      out.push({
        day: s.day,
        startTime: s.startTime,
        endTime: s.endTime,
        location: clinicName,
        clinicId,
        slotStepMinutes: rowStep,
        address: String(c.address || "").trim(),
        city: String(c.city || "").trim(),
        state: String(c.state || "").trim(),
        pincode: String(c.pincode || "").trim(),
        latitude: Number.isFinite(Number(c.latitude)) ? Number(c.latitude) : null,
        longitude: Number.isFinite(Number(c.longitude)) ? Number(c.longitude) : null,
        googlePlaceId: String(c.googlePlaceId || "").trim(),
        phoneNumber: String(c.phoneNumber || "").trim(),
        placePhotoUrl: String(c.placePhotoUrl || "").trim(),
        placePhotoUrls: photoUrlsFromRow({ placePhotoUrl: c.placePhotoUrl, placePhotoUrls: c.placePhotoUrls }),
      });
    });
  });
  return out;
};

const expandRowsToClinicCells = (rows, defaultStepMinutes = 30) => {
  const defaultStep = clampDoctorSlotIntervalMinutes(defaultStepMinutes);
  const map = new Map();
  (Array.isArray(rows) ? rows : []).forEach((r) => {
    const name = String(r.location || "").trim() || "Clinic";
    const clinicId = String(r.clinicId || "").trim() || slugClinicId(name);
    const day = r.day;
    const rawS = toMinutes(r.startTime);
    const rawE = toMinutes(r.endTime);
    if (!Number.isFinite(rawS) || !Number.isFinite(rawE)) return;
    if (!map.has(clinicId)) {
      const initialStep = clampDoctorSlotIntervalMinutes(r.slotStepMinutes ?? r.slotIntervalMinutes ?? defaultStep);
      map.set(clinicId, {
        name,
        cells: new Set(),
        slotIntervalMinutes: initialStep,
        address: String(r.address || "").trim(),
        city: String(r.city || "").trim(),
        state: String(r.state || "").trim(),
        pincode: String(r.pincode || "").trim(),
        latitude: Number.isFinite(Number(r.latitude)) ? Number(r.latitude) : null,
        longitude: Number.isFinite(Number(r.longitude)) ? Number(r.longitude) : null,
        googlePlaceId: String(r.googlePlaceId || "").trim(),
        phoneNumber: String(r.phoneNumber || "").trim(),
        placePhotoUrls: photoUrlsFromRow(r),
        placePhotoUrl: photoUrlsFromRow(r)[0] || "",
      });
    }
    const step = clampDoctorSlotIntervalMinutes(map.get(clinicId).slotIntervalMinutes ?? defaultStep);
    const start = Math.floor(rawS / step) * step;
    const end = Math.ceil(rawE / step) * step;
    if (!DAYS.includes(day) || end <= start) return;
    const prev = map.get(clinicId);
    const mergedUrls = mergePhotoUrls(prev.placePhotoUrls, photoUrlsFromRow(r));
    map.set(clinicId, {
      ...prev,
      address: prev.address || String(r.address || "").trim(),
      city: prev.city || String(r.city || "").trim(),
      state: prev.state || String(r.state || "").trim(),
      pincode: prev.pincode || String(r.pincode || "").trim(),
      latitude:
        prev.latitude != null && Number.isFinite(prev.latitude)
          ? prev.latitude
          : Number.isFinite(Number(r.latitude))
            ? Number(r.latitude)
            : null,
      longitude:
        prev.longitude != null && Number.isFinite(prev.longitude)
          ? prev.longitude
          : Number.isFinite(Number(r.longitude))
            ? Number(r.longitude)
            : null,
      googlePlaceId: prev.googlePlaceId || String(r.googlePlaceId || "").trim(),
      phoneNumber: prev.phoneNumber || String(r.phoneNumber || "").trim(),
      placePhotoUrls: mergedUrls,
      placePhotoUrl: mergedUrls[0] || prev.placePhotoUrl || "",
    });
    const entry = map.get(clinicId);
    for (let t = start; t < end; t += step) {
      entry.cells.add(`${day}|${minutesToHHMM(t)}`);
    }
  });
  return map;
};

const compressClinicCellsToRows = (clinicCellsMap) => {
  const rows = [];
  for (const [clinicId, meta] of clinicCellsMap.entries()) {
    const step = clampDoctorSlotIntervalMinutes(meta.slotIntervalMinutes ?? 30);
    const slotTimes = buildTimeOptionsForInterval(step);
    const name = meta.name;
    const cells = meta.cells;
    const location = String(name || "").trim() || "Clinic";
    DAYS.forEach((day) => {
      const times = slotTimes.filter((t) => cells.has(`${day}|${t}`));
      if (!times.length) return;
      const mins = times.map(toMinutes).sort((a, b) => a - b);
      let start = mins[0];
      let prev = mins[0];
      for (let i = 1; i < mins.length; i += 1) {
        if (mins[i] !== prev + step) {
          rows.push({
            day,
            startTime: minutesToHHMM(start),
            endTime: minutesToHHMM(prev + step),
            location,
            clinicId,
            slotStepMinutes: step,
          });
          start = mins[i];
        }
        prev = mins[i];
      }
      rows.push({
        day,
        startTime: minutesToHHMM(start),
        endTime: minutesToHHMM(prev + step),
        location,
        clinicId,
        slotStepMinutes: step,
      });
    });
  }
  return rows;
};

const buildClinicSchedulesPayload = (clinicCellsMap) => {
  const rows = compressClinicCellsToRows(clinicCellsMap);
  const grouped = new Map();
  rows.forEach((row) => {
    const clinicName = String(row.location || "").trim();
    const clinicId = String(row.clinicId || "").trim() || slugClinicId(clinicName);
    if (!clinicName) return;
    if (!grouped.has(clinicId)) {
      const meta = clinicCellsMap.get(clinicId) || {};
      const urls = mergePhotoUrls(meta.placePhotoUrls, photoUrlsFromRow(meta));
      grouped.set(clinicId, {
        clinicId,
        clinicName,
        slotIntervalMinutes: clampDoctorSlotIntervalMinutes(meta.slotIntervalMinutes ?? 30),
        address: String(meta.address || "").trim(),
        city: String(meta.city || "").trim(),
        state: String(meta.state || "").trim(),
        pincode: String(meta.pincode || "").trim(),
        latitude: Number.isFinite(Number(meta.latitude)) ? Number(meta.latitude) : null,
        longitude: Number.isFinite(Number(meta.longitude)) ? Number(meta.longitude) : null,
        googlePlaceId: String(meta.googlePlaceId || "").trim(),
        phoneNumber: String(meta.phoneNumber || "").trim(),
        placePhotoUrl: urls[0] || "",
        placePhotoUrls: urls,
        slots: [],
      });
    }
    grouped.get(clinicId).slots.push({ day: row.day, startTime: row.startTime, endTime: row.endTime });
  });
  for (const [id, meta] of clinicCellsMap.entries()) {
    if (grouped.has(id)) continue;
    const clinicName = String(meta?.name || "").trim();
    if (clinicName.length < 2) continue;
    const urls = mergePhotoUrls(meta.placePhotoUrls, photoUrlsFromRow(meta));
    grouped.set(id, {
      clinicId: id,
      clinicName,
      slotIntervalMinutes: clampDoctorSlotIntervalMinutes(meta.slotIntervalMinutes ?? 30),
      address: String(meta.address || "").trim(),
      city: String(meta.city || "").trim(),
      state: String(meta.state || "").trim(),
      pincode: String(meta.pincode || "").trim(),
      latitude: Number.isFinite(Number(meta.latitude)) ? Number(meta.latitude) : null,
      longitude: Number.isFinite(Number(meta.longitude)) ? Number(meta.longitude) : null,
      googlePlaceId: String(meta.googlePlaceId || "").trim(),
      phoneNumber: String(meta.phoneNumber || "").trim(),
      placePhotoUrl: urls[0] || "",
      placePhotoUrls: urls,
      slots: [],
    });
  }
  return Array.from(grouped.values());
};

export default function DoctorAvailability() {
  const navigate = useNavigate();
  const isHospitalFlow = tokenManager.getActiveProviderRole() === "hospital";
  const appointmentsListPath = isHospitalFlow ? "/hospital-appointments" : "/doctor-appointments";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [practiceMode, setPracticeMode] = useState("inClinic");
  const [activeDays, setActiveDays] = useState([]);
  const [preferredText, setPreferredText] = useState("");
  /** Profile default when a clinic has no stored interval (legacy doctors). */
  const [defaultSlotIntervalMinutes, setDefaultSlotIntervalMinutes] = useState(30);
  const [selectedClinicId, setSelectedClinicId] = useState("");
  const [clinicCells, setClinicCells] = useState(new Map());
  const [appointments, setAppointments] = useState([]);

  const [slotModal, setSlotModal] = useState(null);
  const [slotModalAction, setSlotModalAction] = useState("add");
  const [slotModalMode, setSlotModalMode] = useState("existing");
  const [slotModalClinicId, setSlotModalClinicId] = useState("");
  const [, setSlotModalNewClinic] = useState("");
  const [slotModalSpanCount, setSlotModalSpanCount] = useState(1);
  const [pendingSlotAfterClinic, setPendingSlotAfterClinic] = useState(null);

  /** Hover / long-press popover for a grid cell (portal to body). */
  const [slotPeek, setSlotPeek] = useState(null);
  const peekTimersRef = useRef({ show: null, hide: null, hold: null });
  const touchHoldOriginRef = useRef({ x: 0, y: 0 });

  const [showAddClinicModal, setShowAddClinicModal] = useState(false);
  /** When set, the add-clinic modal updates this clinic id instead of creating a new one. */
  const [editingClinicId, setEditingClinicId] = useState("");
  const [newClinicNameInput, setNewClinicNameInput] = useState("");
  const [newClinicAddress, setNewClinicAddress] = useState("");
  const [newClinicCity, setNewClinicCity] = useState("");
  const [newClinicState, setNewClinicState] = useState("");
  const [newClinicPincode, setNewClinicPincode] = useState("");
  const [newClinicLat, setNewClinicLat] = useState("");
  const [newClinicLng, setNewClinicLng] = useState("");
  const [newClinicPlaceId, setNewClinicPlaceId] = useState("");
  const [newClinicPhone, setNewClinicPhone] = useState("");
  const [newClinicPhotoUrls, setNewClinicPhotoUrls] = useState([]);
  const [placeSuggestions, setPlaceSuggestions] = useState([]);
  const [isPlaceLoading, setIsPlaceLoading] = useState(false);

  const placesServiceRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const placesSessionTokenRef = useRef(null);
  const placesDebounceRef = useRef(null);

  const saveAvailabilityWithFallback = async (payload) => {
    const attempts = [
      () => API.patch(ENDPOINTS.DOCTOR_UPDATE_AVAILABILITY, payload),
      () => API.put(ENDPOINTS.DOCTOR_UPDATE_AVAILABILITY, payload),
      () => API.post(ENDPOINTS.DOCTOR_UPDATE_AVAILABILITY, payload),
      () => API.patch("/doctor/update-availability", payload),
      () => API.put("/doctor/update-availability", payload),
    ];
    let lastErr = null;
    for (const run of attempts) {
      try {
        return await run();
      } catch (err) {
        lastErr = err;
        const status = err?.response?.status;
        const msg = String(err?.response?.data?.message || err?.message || "").toLowerCase();
        if (!(status === 404 || msg.includes("route not found"))) break;
      }
    }
    throw lastErr || new Error("Could not save availability");
  };

  const loadProfile = useCallback(async () => {
    setLoading(true);
    try {
      const [meRes, apptRes] = await Promise.all([
        API.get(ENDPOINTS.DOCTOR_ME),
        API.get(ENDPOINTS.DOCTOR_APPOINTMENTS, { params: { page: 1, limit: 200 } }),
      ]);
      const p = normalizeMe(meRes?.data);
      setPracticeMode((p?.practiceMode || "inClinic").toLowerCase() === "online" ? "online" : "inClinic");
      setActiveDays(Array.isArray(p?.activeDays) ? p.activeDays.filter((d) => DAYS.includes(d)) : []);
      setPreferredText(String(p?.preferredText || ""));
      const step = clampDoctorSlotIntervalMinutes(p?.slotIntervalMinutes);
      setDefaultSlotIntervalMinutes(step);

      let rows = slotBlocksToDoctorRows(p?.slotBlocks, step);
      if (Array.isArray(p?.clinicSchedules) && p.clinicSchedules.length) {
        rows = [...rows, ...clinicSchedulesToRows(p.clinicSchedules, step)];
      }
      let cellMap = expandRowsToClinicCells(rows, step);
      if (Array.isArray(p?.clinicSchedules) && p.clinicSchedules.length) {
        for (const c of p.clinicSchedules) {
          const clinicName = String(c.clinicName || c.location || "").trim();
          if (clinicName.length < 2) continue;
          const clinicId = String(c.clinicId || "").trim() || slugClinicId(clinicName);
          if (cellMap.has(clinicId)) continue;
          const slots = Array.isArray(c.slots) ? c.slots : [];
          if (slots.length !== 0) continue;
          cellMap.set(clinicId, {
            name: clinicName,
            cells: new Set(),
            slotIntervalMinutes: clampDoctorSlotIntervalMinutes(c.slotIntervalMinutes ?? step),
            address: String(c.address || "").trim(),
            city: String(c.city || "").trim(),
            state: String(c.state || "").trim(),
            pincode: String(c.pincode || "").trim(),
            latitude: Number.isFinite(Number(c.latitude)) ? Number(c.latitude) : null,
            longitude: Number.isFinite(Number(c.longitude)) ? Number(c.longitude) : null,
            googlePlaceId: String(c.googlePlaceId || "").trim(),
            phoneNumber: String(c.phoneNumber || "").trim(),
            placePhotoUrls: photoUrlsFromRow(c),
            placePhotoUrl: photoUrlsFromRow(c)[0] || "",
          });
        }
      }
      if (!cellMap.size && String(p?.hospitalClinicName || "").trim()) {
        const nm = String(p.hospitalClinicName).trim();
        const id = slugClinicId(nm);
        cellMap = new Map([
          [
            id,
            {
              name: nm,
              cells: new Set(),
              slotIntervalMinutes: step,
              address: "",
              city: "",
              state: "",
              pincode: "",
              latitude: null,
              longitude: null,
              googlePlaceId: "",
              phoneNumber: "",
              placePhotoUrl: "",
              placePhotoUrls: [],
            },
          ],
        ]);
      }

      const practiceModeForSave = (p?.practiceMode || "inClinic").toLowerCase() === "online" ? "online" : "inClinic";
      const activeDaysForSave = Array.isArray(p?.activeDays) ? p.activeDays.filter((d) => DAYS.includes(d)) : [];
      const preferredForSave = String(p?.preferredText || "").trim();

      const photoMerge = await mergeStablePlacePhotosIntoClinicMap(cellMap, GOOGLE_MAPS_API_KEY, (placeIds) =>
        API.post(ENDPOINTS.DOCTOR_PLACE_PHOTO_REFS, { placeIds })
      );
      if (photoMerge.changed && photoMerge.map.size > 0) {
        cellMap = photoMerge.map;
        const slotSteps = [...cellMap.values()].map((v) => clampDoctorSlotIntervalMinutes(v.slotIntervalMinutes ?? 30));
        const legacySlotInterval = slotSteps.length ? Math.min(...slotSteps) : step;
        const slotBlocks = compressClinicCellsToRows(cellMap);
        const clinicSchedules = buildClinicSchedulesPayload(cellMap);
        const clearAvailability = slotBlocks.length === 0 && clinicSchedules.length === 0;
        try {
          await saveAvailabilityWithFallback({
            practiceMode: practiceModeForSave,
            activeDays: activeDaysForSave,
            preferredText: preferredForSave.slice(0, 500),
            slotIntervalMinutes: legacySlotInterval,
            slotBlocks,
            clinicSchedules,
            clearAvailability,
          });
        } catch {
          /* DB unchanged; UI still shows refreshed URLs until next save */
        }
      }

      setClinicCells(cellMap);
      const firstId = cellMap.keys().next().value || "";
      setSelectedClinicId((id) => (id && cellMap.has(id) ? id : firstId || ""));

      const rawList = apptRes?.data?.items ?? apptRes?.data?.data ?? apptRes?.data;
      setAppointments(Array.isArray(rawList) ? rawList : []);
    } catch (err) {
      showAlert(err?.response?.data?.message || err?.message || "Could not load availability", { variant: "error", title: "Manage Availability" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const clinics = useMemo(
    () =>
      Array.from(clinicCells.entries())
        .map(([id, c]) => ({
          id,
          name: c.name,
          slotIntervalMinutes: clampDoctorSlotIntervalMinutes(c.slotIntervalMinutes ?? 30),
          address: c.address || "",
          phoneNumber: c.phoneNumber || "",
          placePhotoUrl: c.placePhotoUrl || "",
          placePhotoUrls: Array.isArray(c.placePhotoUrls) && c.placePhotoUrls.length ? c.placePhotoUrls : c.placePhotoUrl ? [c.placePhotoUrl] : [],
          slots: c.cells.size,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [clinicCells]
  );

  useEffect(() => {
    if (!clinics.length) return setSelectedClinicId("");
    if (!selectedClinicId || !clinicCells.has(selectedClinicId)) setSelectedClinicId(clinics[0].id);
  }, [clinics, clinicCells, selectedClinicId]);

  const currentClinic = useMemo(() => {
    const e = clinicCells.get(selectedClinicId);
    return e ? { id: selectedClinicId, ...e } : null;
  }, [clinicCells, selectedClinicId]);

  const resetAddClinicForm = useCallback(() => {
    setNewClinicNameInput("");
    setNewClinicAddress("");
    setNewClinicCity("");
    setNewClinicState("");
    setNewClinicPincode("");
    setNewClinicLat("");
    setNewClinicLng("");
    setNewClinicPlaceId("");
    setNewClinicPhone("");
    setNewClinicPhotoUrls([]);
    setPlaceSuggestions([]);
    setIsPlaceLoading(false);
  }, []);

  const closeAddClinicModal = useCallback(() => {
    resetAddClinicForm();
    setEditingClinicId("");
    setShowAddClinicModal(false);
    setPendingSlotAfterClinic(null);
  }, [resetAddClinicForm]);

  const openAddClinicModalFresh = useCallback(() => {
    setEditingClinicId("");
    resetAddClinicForm();
    setShowAddClinicModal(true);
  }, [resetAddClinicForm]);

  const openEditClinicModal = useCallback(
    (id) => {
      const c = clinicCells.get(id);
      if (!c) return;
      setEditingClinicId(id);
      setNewClinicNameInput(String(c.name || "").trim());
      setNewClinicAddress(String(c.address || "").trim());
      setNewClinicCity(String(c.city || "").trim());
      setNewClinicState(String(c.state || "").trim());
      setNewClinicPincode(String(c.pincode || "").trim());
      setNewClinicLat(c.latitude != null && Number.isFinite(Number(c.latitude)) ? String(c.latitude) : "");
      setNewClinicLng(c.longitude != null && Number.isFinite(Number(c.longitude)) ? String(c.longitude) : "");
      setNewClinicPlaceId(String(c.googlePlaceId || "").trim());
      setNewClinicPhone(String(c.phoneNumber || "").trim());
      const urls = Array.isArray(c.placePhotoUrls) && c.placePhotoUrls.length
        ? [...c.placePhotoUrls]
        : c.placePhotoUrl
          ? [String(c.placePhotoUrl).trim()].filter(Boolean)
          : [];
      setNewClinicPhotoUrls(urls);
      setPendingSlotAfterClinic(null);
      setShowAddClinicModal(true);
    },
    [clinicCells]
  );

  const promptDeleteClinic = useCallback(
    async (id) => {
      const c = clinicCells.get(id);
      if (!c) return;
      const name = String(c.name || "this location").trim() || "this location";
      const slots = c.cells?.size ?? 0;
      const ok = await showConfirm(
        `Remove "${name}"? This clears ${slots} availability slot${slots === 1 ? "" : "s"} on the grid for this clinic. Click "Save Changes" at the top to persist. Existing patient bookings are not cancelled from here.`,
        { title: "Remove clinic", confirmText: "Remove", cancelText: "Cancel", danger: true }
      );
      if (!ok) return;
      setClinicCells((prev) => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
    },
    [clinicCells]
  );

  useEffect(() => {
    if (!showAddClinicModal || !GOOGLE_MAPS_API_KEY) return undefined;

    const initServices = () => {
      if (!window.google?.maps?.places || autocompleteServiceRef.current) return;
      autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
      const node = document.createElement("div");
      placesServiceRef.current = new window.google.maps.places.PlacesService(node);
      placesSessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    };

    if (window.google?.maps?.places) {
      initServices();
      return undefined;
    }

    const existing = document.querySelector('script[data-healbid-google-places="1"]');
    if (existing) {
      existing.addEventListener("load", initServices, { once: true });
      return () => {
        existing.removeEventListener("load", initServices);
      };
    }

    const script = document.createElement("script");
    script.setAttribute("data-healbid-google-places", "1");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(GOOGLE_MAPS_API_KEY)}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", initServices, { once: true });
    document.body.appendChild(script);
    return () => {
      script.removeEventListener("load", initServices);
    };
  }, [showAddClinicModal]);

  useEffect(() => {
    if (!showAddClinicModal || !GOOGLE_MAPS_API_KEY) return undefined;
    if (!autocompleteServiceRef.current || !window.google?.maps?.places) return undefined;

    const query = String(newClinicNameInput || "").trim();
    if (!query || query.length < 2) {
      setPlaceSuggestions([]);
      setIsPlaceLoading(false);
      return undefined;
    }

    if (placesDebounceRef.current) clearTimeout(placesDebounceRef.current);
    setIsPlaceLoading(true);
    placesDebounceRef.current = setTimeout(() => {
      const service = placesServiceRef.current;
      if (!service || !window.google?.maps?.places) {
        setIsPlaceLoading(false);
        return;
      }
      const collected = [];
      const seenPlaceIds = new Set();
      let pagesFetched = 0;
      const MAX_TEXTSEARCH_PAGES = 5;
      const NEXT_PAGE_DELAY_MS = 2100;

      const applyResults = (results = []) => {
        for (const r of results) {
          const placeId = String(r?.place_id || "").trim();
          if (!placeId || seenPlaceIds.has(placeId)) continue;
          seenPlaceIds.add(placeId);
          const photo = r?.photos?.[0];
          const photoUrl =
            typeof photo?.getUrl === "function"
              ? String(photo.getUrl({ maxWidth: 112, maxHeight: 112 }) || "").trim()
              : "";
          collected.push({
            placeId,
            primaryText: String(r?.name || "").trim(),
            secondaryText: String(r?.formatted_address || r?.vicinity || "").trim(),
            description: String(r?.formatted_address || r?.name || "").trim(),
            photoUrl,
          });
        }
        setPlaceSuggestions([...collected]);
      };

      const handlePage = (results, status, pagination) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && Array.isArray(results)) {
          applyResults(results);
          pagesFetched += 1;
          if (pagination?.hasNextPage && pagesFetched < MAX_TEXTSEARCH_PAGES) {
            setTimeout(() => {
              try {
                pagination.nextPage();
              } catch {
                setIsPlaceLoading(false);
              }
            }, NEXT_PAGE_DELAY_MS);
            return;
          }
          setIsPlaceLoading(false);
          return;
        }
        setIsPlaceLoading(false);
      };

      service.textSearch(
        {
          query,
          region: "in",
        },
        handlePage
      );
    }, 180);

    return () => {
      if (placesDebounceRef.current) {
        clearTimeout(placesDebounceRef.current);
        placesDebounceRef.current = null;
      }
    };
  }, [showAddClinicModal, newClinicNameInput]);

  const selectPlaceSuggestion = useCallback((suggestion) => {
    const placeId = String(suggestion?.placeId || "").trim();
    if (!placeId || !placesServiceRef.current || !window.google?.maps?.places) return;
    const service = placesServiceRef.current;
    setIsPlaceLoading(true);
    service.getDetails(
      {
        placeId,
        fields: [
          "name",
          "formatted_address",
          "geometry",
          "address_components",
          "place_id",
          "formatted_phone_number",
          "international_phone_number",
          "photos",
        ],
        sessionToken: placesSessionTokenRef.current || undefined,
      },
      (place, status) => {
        const ok = status === window.google.maps.places.PlacesServiceStatus.OK;
        if (!ok) {
          setIsPlaceLoading(false);
          return;
        }
        const comps = Array.isArray(place?.address_components) ? place.address_components : [];
        const get = (t) => comps.find((c) => Array.isArray(c.types) && c.types.includes(t))?.long_name || "";
        const name = String(place?.name || suggestion?.primaryText || "").trim();
        if (name) setNewClinicNameInput(name);
        setNewClinicAddress(String(place?.formatted_address || suggestion?.description || "").trim());
        setNewClinicCity(get("locality") || get("administrative_area_level_2"));
        setNewClinicState(get("administrative_area_level_1"));
        setNewClinicPincode(get("postal_code"));
        const lat = place?.geometry?.location?.lat?.();
        const lng = place?.geometry?.location?.lng?.();
        setNewClinicLat(Number.isFinite(Number(lat)) ? String(lat) : "");
        setNewClinicLng(Number.isFinite(Number(lng)) ? String(lng) : "");
        setNewClinicPlaceId(String(place?.place_id || placeId));
        const phone = String(place?.formatted_phone_number || place?.international_phone_number || "").trim();
        setNewClinicPhone(phone);
        const fallbackUrls = [];
        try {
          const photos = place?.photos;
          if (photos && photos.length) {
            const n = Math.min(photos.length, MAX_PLACE_PHOTOS);
            for (let i = 0; i < n; i += 1) {
              if (typeof photos[i]?.getUrl !== "function") continue;
              const u = String(photos[i].getUrl({ maxWidth: 480, maxHeight: 480 }) || "").trim();
              if (u && /^https:\/\//i.test(u) && !fallbackUrls.includes(u)) fallbackUrls.push(u);
            }
          }
        } catch {
          /* ignore */
        }

        const finishPick = () => {
          setPlaceSuggestions([]);
          placesSessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
          setIsPlaceLoading(false);
        };

        if (GOOGLE_MAPS_API_KEY && placeId) {
          API.post(ENDPOINTS.DOCTOR_PLACE_PHOTO_REFS, { placeIds: [placeId] })
            .then((res) => {
              const refs = res?.data?.byPlaceId?.[placeId];
              const stable = placePhotoUrlsFromReferences(refs, GOOGLE_MAPS_API_KEY);
              setNewClinicPhotoUrls(stable.length ? stable : fallbackUrls);
            })
            .catch(() => setNewClinicPhotoUrls(fallbackUrls))
            .finally(finishPick);
        } else {
          setNewClinicPhotoUrls(fallbackUrls);
          finishPick();
        }
      }
    );
  }, []);

  const handleClinicSlotIntervalChange = useCallback((clinicId, e) => {
    const next = clampDoctorSlotIntervalMinutes(Number(e.target.value));
    setClinicCells((cells) => {
      const cur = cells.get(clinicId);
      if (!cur) return cells;
      const prevStep = clampDoctorSlotIntervalMinutes(cur.slotIntervalMinutes ?? defaultSlotIntervalMinutes);
      if (prevStep === next) return cells;
      return migrateSingleClinicCells(cells, clinicId, prevStep, next);
    });
  }, [defaultSlotIntervalMinutes]);

  const effectiveGridStep = useMemo(
    () => clampDoctorSlotIntervalMinutes(currentClinic?.slotIntervalMinutes ?? defaultSlotIntervalMinutes),
    [currentClinic, defaultSlotIntervalMinutes]
  );

  const slotTimeOptions = useMemo(() => buildTimeOptionsForInterval(effectiveGridStep), [effectiveGridStep]);

  const modalSlotStep = useMemo(() => {
    const id = slotModalClinicId || selectedClinicId;
    const meta = id ? clinicCells.get(id) : null;
    return clampDoctorSlotIntervalMinutes(meta?.slotIntervalMinutes ?? defaultSlotIntervalMinutes);
  }, [clinicCells, slotModalClinicId, selectedClinicId, defaultSlotIntervalMinutes]);

  const slotModalTimeOptions = useMemo(() => buildTimeOptionsForInterval(modalSlotStep), [modalSlotStep]);

  useEffect(() => {
    if (!slotModal) return;
    const snapped = normalizeToSlotInterval(slotModal.time, modalSlotStep);
    if (snapped !== slotModal.time) setSlotModal((m) => (m ? { ...m, time: snapped } : m));
  }, [slotModal, modalSlotStep]);

  const bookedLookup = useMemo(() => {
    const jsToDay = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const byDayTime = new Map();
    const byClinicId = new Map();
    const byClinicName = new Map();
    const stepForAppointment = (a, rawTs) => {
      const clinicName = String(a?.clinicName || parseDisplayTimeSlot(rawTs).clinicLabel || "").trim();
      const clinicId = String(a?.clinicId || (clinicName ? slugClinicId(clinicName) : "")).trim();
      if (clinicId && clinicCells.has(clinicId)) {
        return clampDoctorSlotIntervalMinutes(clinicCells.get(clinicId).slotIntervalMinutes ?? defaultSlotIntervalMinutes);
      }
      if (clinicName) {
        const hit = [...clinicCells.values()].find((v) => normalizeClinicName(v.name) === normalizeClinicName(clinicName));
        if (hit) return clampDoctorSlotIntervalMinutes(hit.slotIntervalMinutes ?? defaultSlotIntervalMinutes);
      }
      return defaultSlotIntervalMinutes;
    };
    appointments.forEach((a) => {
      const rawDate = a?.doctorProposedDate || a?.appointmentDate;
      const dateStr = typeof rawDate === "string" ? rawDate.trim() : "";
      const d =
        /^\d{4}-\d{2}-\d{2}$/.test(dateStr)
          ? new Date(`${dateStr}T12:00:00`)
          : new Date(rawDate || "");
      if (Number.isNaN(d.getTime())) return;
      const day = jsToDay[d.getDay()];
      const rawTs = a?.doctorProposedTimeSlot || a?.timeSlot;
      const step = stepForAppointment(a, rawTs);
      const time = normalizeAppointmentTime(rawTs, step);
      if (!day || !time) return;
      const patientName = String(a?.patientName || a?.patient?.name || "Patient").trim() || "Patient";
      const patientMobile = String(a?.patient?.mobile || a?.patientMobile || "").trim();
      const procedure = String(a?.subCategoryName || a?.subCategory?.name || "").trim();
      const detail = {
        patientName,
        status: String(a?.status || a?.appointmentStatus || "booked").trim() || "booked",
        appointmentId: String(a?._id || a?.id || "").trim(),
        patientId: patientIdFromAppointment(a),
        patientMobile,
        procedure,
      };
      byDayTime.set(`${day}|${time}`, detail);
      const clinicName = String(a?.clinicName || parseDisplayTimeSlot(rawTs).clinicLabel || "").trim();
      const clinicId = String(a?.clinicId || (clinicName ? slugClinicId(clinicName) : "")).trim();
      if (clinicId) byClinicId.set(`${clinicId}|${day}|${time}`, detail);
      if (clinicName) byClinicName.set(`${normalizeClinicName(clinicName)}|${day}|${time}`, detail);
    });
    return { byDayTime, byClinicId, byClinicName };
  }, [appointments, clinicCells, defaultSlotIntervalMinutes]);

  const getBookedDetail = useCallback((day, time, clinicOverride = null) => {
    const id = clinicOverride?.id ?? currentClinic?.id;
    const name = clinicOverride?.name ?? currentClinic?.name;
    if (!id && !name) return null;
    const byId = id ? bookedLookup.byClinicId.get(`${id}|${day}|${time}`) : null;
    const byName = name ? bookedLookup.byClinicName.get(`${normalizeClinicName(name)}|${day}|${time}`) : null;
    if (clinicOverride) return byId || byName || null;
    return byId || byName || bookedLookup.byDayTime.get(`${day}|${time}`) || null;
  }, [bookedLookup, currentClinic]);

  const getCellState = (day, time) => {
    if (!currentClinic) return "off";
    if (!currentClinic.cells.has(`${day}|${time}`)) return "off";
    return getBookedDetail(day, time) ? "booked" : "available";
  };

  const openSlotModal = (day, time, action = "add") => {
    const step = clampDoctorSlotIntervalMinutes(currentClinic?.slotIntervalMinutes ?? defaultSlotIntervalMinutes);
    setSlotModal({ day, time: normalizeToSlotInterval(time, step) });
    setSlotModalAction(action === "edit" ? "edit" : "add");
    setSlotModalMode("existing");
    setSlotModalClinicId(currentClinic?.id || "");
    setSlotModalNewClinic("");
    setSlotModalSpanCount(1);
  };

  const openBookedSlotDetails = useCallback(
    (day, time, booked) => {
      if (!currentClinic || !booked) return;
      const when = `${day} ${formatDisplayTime(time)}`;
      const bits = [
        `Patient: ${booked.patientName}`,
        booked.patientMobile ? `Mobile: ${booked.patientMobile}` : null,
        booked.procedure ? `Procedure: ${booked.procedure}` : null,
        `Clinic: ${currentClinic.name}`,
        `When: ${when}`,
        `Status: ${booked.status}`,
      ].filter(Boolean);
      const search = booked.patientName || booked.patientMobile || "";
      showAlert(bits.join(" · "), {
        title: "Booked slot",
        variant: "info",
        duration: 0,
        actions: [
          {
            label: "Open appointments",
            kind: "primary",
            onClick: () => {
              if (search) {
                navigate(`${appointmentsListPath}?search=${encodeURIComponent(search)}`);
              } else {
                navigate(appointmentsListPath);
              }
            },
          },
          { label: "Close", kind: "secondary", onClick: () => {} },
        ],
      });
    },
    [appointmentsListPath, currentClinic, navigate]
  );

  const toggleCell = (day, time) => {
    if (!currentClinic) return;
    const state = getCellState(day, time);
    const booked = getBookedDetail(day, time);
    if (booked) {
      openBookedSlotDetails(day, time, booked);
      return;
    }
    if (state === "off") {
      // Clinic already chosen: add availability for it (do not force new-clinic flow).
      if (currentClinic) {
        openSlotModal(day, time, "add");
        return;
      }
      setPendingSlotAfterClinic({ day, time, spanCount: 1, action: "add" });
      setEditingClinicId("");
      setShowAddClinicModal(true);
      return;
    }
    return openSlotModal(day, time, "edit");
  };

  const confirmAddClinic = async () => {
    const trimmedName = String(newClinicNameInput || "").trim();
    if (trimmedName.length < 2) {
      showAlert("Clinic name must be at least 2 characters (required to save).", {
        variant: "warning",
        title: "Manage Availability",
      });
      return;
    }

    const clinicSavedMessage = "Clinic saved to your profile.";

    if (editingClinicId) {
      const existingEntry = clinicCells.get(editingClinicId);
      if (!existingEntry) {
        showAlert("This clinic is no longer in your list. Close and refresh the page, then try again.", {
          variant: "warning",
          title: "Manage Availability",
        });
        return;
      }
      const dup = [...clinicCells.entries()].some(
        ([otherId, v]) =>
          otherId !== editingClinicId && normalizeClinicName(v.name) === normalizeClinicName(trimmedName)
      );
      if (dup) {
        showAlert("Another clinic already uses this name.", { variant: "warning" });
        return;
      }
      const next = new Map(clinicCells);
      const cur = next.get(editingClinicId);
      if (!cur) return;
      const mergedPhotos = photoUrlsFromRow({
        placePhotoUrls: newClinicPhotoUrls,
        placePhotoUrl: newClinicPhotoUrls[0] || "",
      });
      next.set(editingClinicId, {
        ...cur,
        name: trimmedName,
        address: String(newClinicAddress || "").trim(),
        city: String(newClinicCity || "").trim(),
        state: String(newClinicState || "").trim(),
        pincode: String(newClinicPincode || "").trim(),
        latitude: Number.isFinite(Number(newClinicLat)) ? Number(newClinicLat) : null,
        longitude: Number.isFinite(Number(newClinicLng)) ? Number(newClinicLng) : null,
        googlePlaceId: String(newClinicPlaceId || "").trim(),
        phoneNumber: String(newClinicPhone || "").trim(),
        placePhotoUrls: mergedPhotos,
        placePhotoUrl: mergedPhotos[0] || "",
      });
      setClinicCells(next);
      setSelectedClinicId(editingClinicId);
      setPendingSlotAfterClinic(null);
      resetAddClinicForm();
      setEditingClinicId("");
      setShowAddClinicModal(false);
      await persistAvailabilityForCellsMap(next, { successMessage: clinicSavedMessage });
      return;
    }

    const payload = {
      name: newClinicNameInput,
      address: newClinicAddress,
      city: newClinicCity,
      state: newClinicState,
      pincode: newClinicPincode,
      latitude: newClinicLat,
      longitude: newClinicLng,
      googlePlaceId: newClinicPlaceId,
      phoneNumber: newClinicPhone,
      placePhotoUrls: newClinicPhotoUrls,
      placePhotoUrl: newClinicPhotoUrls[0] || "",
      slotIntervalMinutes: defaultSlotIntervalMinutes,
    };
    const ins = insertClinicIntoMapCopy(clinicCells, payload);
    if (!ins.ok) {
      if (ins.reason === "duplicate") {
        showAlert("Another clinic already uses this name.", { variant: "warning", title: "Manage Availability" });
      }
      return;
    }
    let final = ins.next;
    if (pendingSlotAfterClinic) {
      final = applySlotsToClinicMapCopy(
        final,
        ins.id,
        trimmedName,
        pendingSlotAfterClinic.day,
        pendingSlotAfterClinic.time,
        pendingSlotAfterClinic.spanCount || 1,
        (d, t) => getBookedDetail(d, t, { id: ins.id, name: trimmedName }),
        defaultSlotIntervalMinutes
      );
    }
    setClinicCells(final);
    setSelectedClinicId(ins.id);
    setPendingSlotAfterClinic(null);
    resetAddClinicForm();
    setEditingClinicId("");
    setShowAddClinicModal(false);
    await persistAvailabilityForCellsMap(final, { successMessage: clinicSavedMessage });
  };

  const openAdvancedClinicFormFromSlot = () => {
    if (!slotModal) return;
    setPendingSlotAfterClinic({
      day: slotModal.day,
      time: slotModal.time,
      spanCount: slotModalSpanCount,
      action: slotModalAction,
    });
    setSlotModal(null);
    setEditingClinicId("");
    setShowAddClinicModal(true);
  };

  const confirmAddSlot = async () => {
    if (!slotModal || saving) return;
    const day = slotModal.day;
    const startIdx = slotModalTimeOptions.indexOf(slotModal.time);
    if (startIdx < 0) return;

    const clinicId = slotModalClinicId;
    const existing = clinics.find((c) => c.id === slotModalClinicId);
    if (!existing) {
      showAlert("Select clinic", { variant: "warning" });
      return;
    }
    const clinicName = existing.name;
    const count = Math.max(1, Math.min(12, Number(slotModalSpanCount) || 1));

    const next = new Map(clinicCells);
    if (slotModalAction === "edit" && currentClinic?.id) {
      const source = next.get(currentClinic.id);
      if (source) {
        const sourceSet = new Set(source.cells || []);
        for (let i = 0; i < count; i += 1) {
          const t = slotModalTimeOptions[startIdx + i];
          if (!t) break;
          if (!getBookedDetail(day, t)) sourceSet.delete(`${day}|${t}`);
        }
        next.set(currentClinic.id, { ...source, cells: sourceSet });
      }
    }

    const cur = next.get(clinicId) || {
      name: clinicName,
      cells: new Set(),
      slotIntervalMinutes: modalSlotStep,
      address: "",
      city: "",
      state: "",
      pincode: "",
      latitude: null,
      longitude: null,
      googlePlaceId: "",
      phoneNumber: "",
      placePhotoUrl: "",
      placePhotoUrls: [],
    };
    const set = new Set(cur.cells || []);
    for (let i = 0; i < count; i += 1) {
      const t = slotModalTimeOptions[startIdx + i];
      if (!t) break;
      if (!getBookedDetail(day, t, { id: clinicId, name: clinicName })) set.add(`${day}|${t}`);
    }
    next.set(clinicId, { ...cur, name: clinicName || cur.name, cells: set });

    setClinicCells(next);
    setSelectedClinicId(clinicId);
    setSlotModal(null);
    await persistAvailabilityForCellsMap(next, { successMessage: "Slot saved to your profile." });
  };

  const confirmDeleteSlot = async () => {
    if (!slotModal || !currentClinic || saving) return;
    const day = slotModal.day;
    const deleteOptions = buildTimeOptionsForInterval(
      clampDoctorSlotIntervalMinutes(currentClinic.slotIntervalMinutes ?? defaultSlotIntervalMinutes)
    );
    const startIdx = deleteOptions.indexOf(slotModal.time);
    if (startIdx < 0) return;
    const count = Math.max(1, Math.min(12, Number(slotModalSpanCount) || 1));
    const next = new Map(clinicCells);
    const cur = next.get(currentClinic.id);
    if (!cur) return;
    const set = new Set(cur.cells || []);
    for (let i = 0; i < count; i += 1) {
      const t = deleteOptions[startIdx + i];
      if (!t) break;
      if (!getBookedDetail(day, t)) set.delete(`${day}|${t}`);
    }
    next.set(currentClinic.id, { ...cur, cells: set });
    setClinicCells(next);
    setSlotModal(null);
    await persistAvailabilityForCellsMap(next, { successMessage: "Slot removed from your profile." });
  };

  const applyDayAll = (day, mode) => {
    if (!currentClinic) return;
    setClinicCells((prev) => {
      const next = new Map(prev);
      const cur = next.get(currentClinic.id) || currentClinic;
      const set = new Set(cur.cells || []);
      slotTimeOptions.forEach((t) => {
        const key = `${day}|${t}`;
        if (getBookedDetail(day, t)) return;
        if (mode === "add") set.add(key); else set.delete(key);
      });
      next.set(currentClinic.id, { ...cur, cells: set });
      return next;
    });
  };

  const persistAvailabilityForCellsMap = useCallback(
    async (cellsMap, opts = {}) => {
      const steps = [...cellsMap.values()].map((v) => clampDoctorSlotIntervalMinutes(v.slotIntervalMinutes ?? 30));
      const legacySlotInterval = steps.length ? Math.min(...steps) : defaultSlotIntervalMinutes;
      const slotBlocks = compressClinicCellsToRows(cellsMap);
      const clinicSchedules = buildClinicSchedulesPayload(cellsMap);
      const clearAvailability = slotBlocks.length === 0 && clinicSchedules.length === 0;
      setSaving(true);
      try {
        await saveAvailabilityWithFallback({
          practiceMode,
          activeDays,
          preferredText: preferredText.trim().slice(0, 500),
          slotIntervalMinutes: legacySlotInterval,
          slotBlocks,
          clinicSchedules,
          clearAvailability,
        });
        showAlert(opts.successMessage || "Availability updated successfully.", { variant: "success" });
        await loadProfile();
      } catch (err) {
        showAlert(err?.response?.data?.message || err?.message || "Could not save availability", {
          variant: "error",
          title: "Manage Availability",
        });
        await loadProfile();
      } finally {
        setSaving(false);
      }
    },
    [activeDays, loadProfile, practiceMode, preferredText, defaultSlotIntervalMinutes]
  );

  const save = async () => {
    await persistAvailabilityForCellsMap(clinicCells);
  };

  const timeRows = useMemo(() => {
    return slotTimeOptions.filter((t) => {
      const mins = toMinutes(t);
      return mins >= 9 * 60 && mins <= 21 * 60;
    });
  }, [slotTimeOptions]);

  const dayLabelByDate = useMemo(() => {
    const now = new Date();
    const map = {};
    DAYS.forEach((d) => {
      const date = new Date(now);
      const dayIndex = DAYS.indexOf(d);
      const jsDay = date.getDay();
      const conv = jsDay === 0 ? 6 : jsDay - 1;
      const diff = dayIndex - conv;
      date.setDate(now.getDate() + diff);
      map[d] = `${d} ${String(date.getDate()).padStart(2, "0")}`;
    });
    return map;
  }, []);

  const clearPeekTimers = useCallback(() => {
    const t = peekTimersRef.current;
    if (t.show) window.clearTimeout(t.show);
    if (t.hide) window.clearTimeout(t.hide);
    if (t.hold) window.clearTimeout(t.hold);
    peekTimersRef.current = { show: null, hide: null, hold: null };
  }, []);

  useEffect(() => () => clearPeekTimers(), [clearPeekTimers]);

  useEffect(() => {
    if (!slotModal) return;
    clearPeekTimers();
    setSlotPeek(null);
  }, [slotModal, clearPeekTimers]);

  const beginCellPeek = useCallback(
    (day, time, state, booked, anchorEl) => {
      if (!anchorEl || state === "off") return;
      window.clearTimeout(peekTimersRef.current.hide);
      peekTimersRef.current.hide = null;
      window.clearTimeout(peekTimersRef.current.show);
      const rect = anchorEl.getBoundingClientRect();
      const anchor = {
        top: rect.top,
        left: rect.left,
        bottom: rect.bottom,
        right: rect.right,
        width: rect.width,
        height: rect.height,
      };
      peekTimersRef.current.show = window.setTimeout(() => {
        setSlotPeek({ day, time, state, booked, anchor });
      }, SLOT_PEEK_HOVER_MS);
    },
    []
  );

  const endCellPeekSoon = useCallback(() => {
    window.clearTimeout(peekTimersRef.current.show);
    peekTimersRef.current.show = null;
    peekTimersRef.current.hide = window.setTimeout(() => {
      setSlotPeek(null);
    }, SLOT_PEEK_HIDE_MS);
  }, []);

  const keepPeekOpen = useCallback(() => {
    window.clearTimeout(peekTimersRef.current.hide);
    peekTimersRef.current.hide = null;
  }, []);

  useEffect(() => {
    if (!slotPeek) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        clearPeekTimers();
        setSlotPeek(null);
      }
    };
    const onDown = (e) => {
      const el = e.target;
      if (el.closest?.(".doc_av_slotpeek")) return;
      if (el.closest?.(".doc_av_cell")) return;
      clearPeekTimers();
      setSlotPeek(null);
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown, true);
    };
  }, [slotPeek, clearPeekTimers]);

  const beginCellPeekHold = useCallback(
    (day, time, state, booked, anchorEl) => {
      if (!anchorEl || state === "off") return;
      clearPeekTimers();
      const rect = anchorEl.getBoundingClientRect();
      const anchor = {
        top: rect.top,
        left: rect.left,
        bottom: rect.bottom,
        right: rect.right,
        width: rect.width,
        height: rect.height,
      };
      peekTimersRef.current.hold = window.setTimeout(() => {
        setSlotPeek({ day, time, state, booked, anchor, fromHold: true });
      }, SLOT_PEEK_HOLD_MS);
    },
    [clearPeekTimers]
  );

  const cancelCellPeekHold = useCallback(() => {
    window.clearTimeout(peekTimersRef.current.hold);
    peekTimersRef.current.hold = null;
  }, []);

  const onCellTouchMove = useCallback(
    (e) => {
      if (!e.touches?.[0]) return;
      const t = e.touches[0];
      const { x, y } = touchHoldOriginRef.current;
      if (Math.abs(t.clientX - x) > 14 || Math.abs(t.clientY - y) > 14) {
        cancelCellPeekHold();
      }
    },
    [cancelCellPeekHold]
  );

  const slotPeekPosition = useMemo(() => {
    if (!slotPeek?.anchor) return null;
    const { top, left, bottom, width } = slotPeek.anchor;
    const margin = 8;
    const estH = 200;
    const vh = typeof window !== "undefined" ? window.innerHeight : 800;
    const vw = typeof window !== "undefined" ? window.innerWidth : 400;
    let topPx = bottom + margin;
    if (topPx + estH > vh - 12) topPx = Math.max(8, top - estH - margin);
    const cx = left + width / 2;
    const half = 138;
    const leftPx = Math.min(vw - half - 10, Math.max(half + 10, cx));
    return { top: topPx, left: leftPx, transform: "translateX(-50%)" };
  }, [slotPeek]);

  return (
    <DoctorLayout>
      <div className="doc_av_page">
        <div className="doc_av_head doc_av_head--compact">
          <button type="button" className="doc_av_save_top" onClick={save} disabled={saving || loading}>{saving ? "Saving..." : "Save Changes"}</button>
        </div>

        {!loading && (
          <>
            <section className="doc_av_card">
              <h3>1. Select clinic / location</h3>
              <div className="doc_av_clinics">
                {clinics.map((c) => (
                  <div
                    key={c.id}
                    className={`doc_av_clinic_shell${selectedClinicId === c.id ? " doc_av_clinic_shell--active" : ""}`}
                  >
                    <button type="button" className="doc_av_clinic" onClick={() => setSelectedClinicId(c.id)}>
                      <div className="doc_av_clinic_top">
                        {c.placePhotoUrls?.length ? (
                          <div className="doc_av_clinic_thumbs" aria-hidden>
                            {c.placePhotoUrls.slice(0, 4).map((url, idx) => (
                              <img key={idx} className="doc_av_clinic_thumb doc_av_clinic_thumb--multi" src={url} alt="" loading="lazy" />
                            ))}
                            {c.placePhotoUrls.length > 4 ? <span className="doc_av_clinic_thumb_more">+{c.placePhotoUrls.length - 4}</span> : null}
                          </div>
                        ) : (
                          <span className="doc_av_clinic_thumb doc_av_clinic_thumb--ph" aria-hidden />
                        )}
                        <div className="doc_av_clinic_text">
                          <strong>{c.name}</strong>
                          <span>{c.slots} slots</span>
                          {c.phoneNumber ? (
                            <a className="doc_av_clinic_phone" href={`tel:${String(c.phoneNumber).replace(/\s/g, "")}`} onClick={(e) => e.stopPropagation()}>
                              {c.phoneNumber}
                            </a>
                          ) : null}
                          {c.address ? <span className="doc_av_clinic_addr">{c.address}</span> : null}
                          <span className="doc_av_clinic_id">{c.id}</span>
                        </div>
                      </div>
                    </button>
                    <div className="doc_av_clinic_toolbar" role="group" aria-label={`Actions for ${c.name}`}>
                      <label className="doc_av_clinic_interval" onClick={(e) => e.stopPropagation()}>
                        <span className="doc_av_clinic_interval_label">Slot interval</span>
                        <select
                          value={c.slotIntervalMinutes}
                          onChange={(e) => handleClinicSlotIntervalChange(c.id, e)}
                          aria-label={`Slot length for ${c.name}`}
                        >
                          {DOCTOR_SLOT_INTERVAL_CHOICES.map((m) => (
                            <option key={m} value={m}>
                              {m} min
                            </option>
                          ))}
                        </select>
                      </label>
                      <button
                        type="button"
                        className="doc_av_clinic_act"
                        onClick={() => openEditClinicModal(c.id)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="doc_av_clinic_act doc_av_clinic_act--danger"
                        onClick={() => promptDeleteClinic(c.id)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
                <button type="button" className="doc_av_clinic doc_av_clinic--add" onClick={openAddClinicModalFresh}>
                  <span className="doc_av_clinic--add_icon" aria-hidden>+</span>
                  Add New Clinic
                </button>
              </div>
            </section>

            <section className="doc_av_card">
              <div className="doc_av_toolbar">
                <p className="doc_av_toolbar_hint">Slot length is set per clinic on each location card.</p>
                <button
                  type="button"
                  className="doc_av_toolbar_btn"
                  title="Fill every day: mark all non-booked cells as available for the selected clinic"
                  onClick={() => DAYS.forEach((d) => applyDayAll(d, "add"))}
                >
                  Bulk fill week
                </button>
                <button type="button" className="doc_av_toolbar_btn doc_av_toolbar_btn--muted" title="Clear all available slots for the selected clinic (booked slots stay)" onClick={() => DAYS.forEach((d) => applyDayAll(d, "remove"))}>
                  Clear week
                </button>
                <div className="doc_av_legend"><span><i className="available" /> Available</span><span><i className="booked" /> Booked</span><span><i className="off" /> Unavailable</span></div>
              </div>

              <div className="doc_av_daystrip">
                <div />
                {DAYS.map((d) => <button key={d} type="button" className="doc_av_daybtn" onClick={() => applyDayAll(d, "add")}><strong>{dayLabelByDate[d]}</strong><span>Fill day</span></button>)}
              </div>

              <div className="doc_av_grid">
                {timeRows.map((time) => (
                  <React.Fragment key={time}>
                    <div className="doc_av_time">{formatDisplayTime(time)}</div>
                    {DAYS.map((d) => {
                      const state = getCellState(d, time);
                      const booked = state === "booked" ? getBookedDetail(d, time) : null;
                      const peekHandlers =
                        state === "off"
                          ? {}
                          : {
                              onMouseEnter: (e) => beginCellPeek(d, time, state, booked, e.currentTarget),
                              onMouseLeave: endCellPeekSoon,
                              onFocus: (e) => beginCellPeek(d, time, state, booked, e.currentTarget),
                              onBlur: endCellPeekSoon,
                              onTouchStart: (e) => {
                                const t0 = e.touches?.[0];
                                if (t0) touchHoldOriginRef.current = { x: t0.clientX, y: t0.clientY };
                                beginCellPeekHold(d, time, state, booked, e.currentTarget);
                              },
                              onTouchMove: onCellTouchMove,
                              onTouchEnd: cancelCellPeekHold,
                              onTouchCancel: cancelCellPeekHold,
                            };
                      return (
                        <button
                          key={`${d}-${time}`}
                          type="button"
                          className={`doc_av_cell doc_av_cell--${state}`}
                          onClick={() => toggleCell(d, time)}
                          aria-label={`${dayLabelByDate[d] || d} ${formatDisplayTime(time)}, ${state}`}
                          {...peekHandlers}
                        >
                          {state === "booked" ? `Booked${booked?.patientName ? ` • ${booked.patientName}` : ""}` : state === "available" ? "✓" : "+"}
                        </button>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>

              <div className="doc_av_days">
                <span>Support days:</span>
                {DAYS.map((d) => <label key={d} className={`doc_av_daychip ${activeDays.includes(d) ? "active" : ""}`}><input type="checkbox" checked={activeDays.includes(d)} onChange={() => setActiveDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]))} />{d}</label>)}
              </div>

              <textarea className="doc_av_note" rows={3} value={preferredText} onChange={(e) => setPreferredText(e.target.value)} placeholder="Optional note for patients..." />
            </section>
          </>
        )}
      </div>

      {slotModal && (
        <div className="doc_av_modal_backdrop" role="presentation" onClick={() => (saving ? null : setSlotModal(null))}>
          <div
            className="doc_av_modal doc_av_modal--slot"
            role="dialog"
            aria-modal="true"
            aria-labelledby="doc_av_slot_modal_title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="doc_av_slotmodal__header">
              <div>
                <h3 id="doc_av_slot_modal_title">{slotModalAction === "edit" ? "Edit slot" : "Add slot"}</h3>
                <div className="doc_av_slotmodal__meta" aria-label="Slot context">
                  <span className="doc_av_slotmodal__meta-day">{slotModal.day}</span>
                  <span className="doc_av_slotmodal__meta-sep" aria-hidden>
                    ·
                  </span>
                  <span className="doc_av_slotmodal__meta-time">{formatDisplayTime(slotModal.time)}</span>
                  {currentClinic?.name ? (
                    <>
                      <span className="doc_av_slotmodal__meta-sep" aria-hidden>
                        ·
                      </span>
                      <span className="doc_av_slotmodal__meta-clinic">{currentClinic.name}</span>
                    </>
                  ) : null}
                </div>
              </div>
              <button type="button" className="doc_av_slotmodal__close" onClick={() => (saving ? null : setSlotModal(null))} disabled={saving} aria-label="Close">
                ×
              </button>
            </div>

            <div className="doc_av_slotmodal__body">
              <fieldset className="doc_av_slotmodal__fieldset">
                <legend className="doc_av_slotmodal__legend">Clinic for this slot</legend>
                <div className="doc_av_slotmodal__mode-row" role="group" aria-label="Clinic source">
                  <button
                    type="button"
                    className={`doc_av_slotmodal__mode${slotModalMode === "existing" ? " doc_av_slotmodal__mode--active" : ""}`}
                    onClick={() => setSlotModalMode("existing")}
                  >
                    Existing clinic
                  </button>
                  <button type="button" className="doc_av_slotmodal__mode" onClick={openAdvancedClinicFormFromSlot}>
                    New clinic
                  </button>
                </div>
                <div className="doc_av_slotmodal__control">
                  <label className="doc_av_slotmodal__label" htmlFor="doc_av_slot_clinic_select">
                    Which location?
                  </label>
                  <select
                    id="doc_av_slot_clinic_select"
                    className="doc_av_slotmodal__select"
                    value={slotModalClinicId}
                    onChange={(e) => setSlotModalClinicId(e.target.value)}
                  >
                    <option value="">Select clinic…</option>
                    {clinics.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              </fieldset>

              <div className="doc_av_slotmodal__fieldset doc_av_slotmodal__fieldset--span">
                <label className="doc_av_slotmodal__legend doc_av_slotmodal__legend--static" htmlFor="slotSpanCount">
                  {slotModalAction === "edit" ? "Slots to edit or clear" : "Consecutive slots"}
                </label>
                <select
                  id="slotSpanCount"
                  className="doc_av_slotmodal__select"
                  value={slotModalSpanCount}
                  onChange={(e) => setSlotModalSpanCount(Number(e.target.value))}
                >
                  {[1, 2, 3, 4, 6, 8].map((n) => (
                    <option key={n} value={n}>
                      {buildSlotSpanLabel(slotModal.time, n, modalSlotStep)}
                    </option>
                  ))}
                </select>
                <div className="doc_av_slotmodal__summary" role="status">
                  <span className="doc_av_slotmodal__summary-label">Selected</span>
                  <span className="doc_av_slotmodal__summary-value">
                    {buildSlotSpanLabel(slotModal.time, slotModalSpanCount, modalSlotStep)}
                  </span>
                </div>
              </div>
            </div>

            <div className="doc_av_slotmodal__footer">
              <button type="button" className="doc_av_slotmodal__btn doc_av_slotmodal__btn--ghost" onClick={() => (saving ? null : setSlotModal(null))} disabled={saving}>
                Cancel
              </button>
              {slotModalAction === "edit" ? (
                <button type="button" className="doc_av_slotmodal__btn doc_av_slotmodal__btn--danger" onClick={() => void confirmDeleteSlot()} disabled={saving}>
                  {saving ? "Saving…" : "Delete slots"}
                </button>
              ) : null}
              <button type="button" className="doc_av_slotmodal__btn doc_av_slotmodal__btn--primary" onClick={() => void confirmAddSlot()} disabled={saving || loading}>
                {saving ? "Saving…" : "Save slot"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddClinicModal && (
        <div
          className="doc_av_modal_backdrop doc_av_modal_backdrop--addclinic"
          role="presentation"
          onClick={() => {
            if (!saving) closeAddClinicModal();
          }}
        >
          <div
            className="doc_av_modal doc_av_modal--addclinic doc_av_addclinic"
            role="dialog"
            aria-modal="true"
            aria-labelledby="doc_av_addclinic_title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="doc_av_addclinic_header">
              <div>
                <h3 id="doc_av_addclinic_title">{editingClinicId ? "Edit clinic" : "Add clinic"}</h3>
                <p className="doc_av_addclinic_lead">
                  {editingClinicId
                    ? "Update name, address, phone, or photos. Save clinic writes this clinic to your profile immediately."
                    : "Search Google Places, pick a result, then review or edit details. Add clinic saves the new location to your profile immediately."}
                </p>
              </div>
              <button type="button" className="doc_av_addclinic_close" onClick={closeAddClinicModal} disabled={saving} aria-label="Close">
                ×
              </button>
            </div>

            <div className="doc_av_addclinic_body_scroll">
            <section className="doc_av_addclinic_section">
              <span className="doc_av_addclinic_kicker">Step 1</span>
              <label className="doc_av_addclinic_label" htmlFor="doc_av_place_search">
                Find your clinic
              </label>
              <div className="doc_av_addclinic_search_wrap">
                <span className="doc_av_addclinic_search_icon" aria-hidden>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <circle cx="11" cy="11" r="7" />
                    <path d="M21 21l-4.3-4.3" />
                  </svg>
                </span>
                <input
                  id="doc_av_place_search"
                  className="doc_av_addclinic_input doc_av_addclinic_input--search"
                  type="text"
                  value={newClinicNameInput}
                  onChange={(e) => {
                    setNewClinicNameInput(e.target.value);
                    setNewClinicPlaceId("");
                    if (!String(e.target.value || "").trim()) setNewClinicPhotoUrls([]);
                  }}
                  placeholder="Type name or area, then choose from the list"
                  autoComplete="off"
                />
              </div>
              {!!placeSuggestions.length && (
                <div className="doc_av_place_suggestions" role="listbox" aria-label="Google Places suggestions">
                  {placeSuggestions.map((item) => (
                    <button
                      key={item.placeId}
                      type="button"
                      className="doc_av_place_suggestion"
                      role="option"
                      aria-selected={String(newClinicPlaceId || "").trim() === String(item.placeId || "").trim()}
                      onClick={() => selectPlaceSuggestion(item)}
                    >
                      <span className="doc_av_place_suggestion_media" aria-hidden>
                        {item.photoUrl ? <img src={item.photoUrl} alt="" loading="lazy" /> : "📍"}
                      </span>
                      <span className="doc_av_place_suggestion_text">
                        <strong title={item.primaryText}>{item.primaryText}</strong>
                        {item.secondaryText ? <small title={item.secondaryText}>{item.secondaryText}</small> : null}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {isPlaceLoading ? <p className="doc_av_addclinic_hint">Searching Google Places…</p> : null}
              <p className="doc_av_addclinic_hint">Choosing a suggestion fills address, phone, and photos when Google provides them.</p>
            </section>

            <section className="doc_av_addclinic_section doc_av_addclinic_section--split">
              <div className="doc_av_addclinic_media">
                <span className="doc_av_addclinic_kicker">{newClinicPhotoUrls.length > 1 ? "Photos" : "Photo"}</span>
                {newClinicPhotoUrls.length ? (
                  <div className="doc_av_place_gallery" role="list">
                    {newClinicPhotoUrls.map((url, idx) => (
                      <div key={idx} className="doc_av_place_preview doc_av_place_preview--card doc_av_place_preview--tile" role="listitem">
                        <img src={url} alt="" loading="lazy" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="doc_av_place_empty" role="status">
                    <span>No photos yet</span>
                    <small>Shown after you pick a place that has images in Google.</small>
                  </div>
                )}
              </div>
              <div className="doc_av_addclinic_contact">
                <span className="doc_av_addclinic_kicker">Contact</span>
                <label className="doc_av_addclinic_label" htmlFor="doc_av_clinic_phone">
                  Phone
                </label>
                <input
                  id="doc_av_clinic_phone"
                  className="doc_av_addclinic_input"
                  type="tel"
                  inputMode="tel"
                  value={newClinicPhone}
                  onChange={(e) => setNewClinicPhone(e.target.value)}
                  placeholder="e.g. +91 … (auto-filled from Google)"
                />
              </div>
            </section>

            {pendingSlotAfterClinic ? (
              <section className="doc_av_addclinic_section">
                <span className="doc_av_addclinic_kicker">Slot setup</span>
                <div className="doc_av_modal_row">
                  <label htmlFor="slotSpanCountFromClinic">
                    Add consecutive slots ({pendingSlotAfterClinic.day} at {formatDisplayTime(pendingSlotAfterClinic.time)})
                  </label>
                  <select
                    id="slotSpanCountFromClinic"
                    value={pendingSlotAfterClinic.spanCount || 1}
                    onChange={(e) =>
                      setPendingSlotAfterClinic((prev) =>
                        prev ? { ...prev, spanCount: Number(e.target.value) || 1 } : prev
                      )
                    }
                  >
                    {[1, 2, 3, 4, 6, 8].map((n) => (
                      <option key={n} value={n}>
                        {buildSlotSpanLabel(pendingSlotAfterClinic.time, n, defaultSlotIntervalMinutes)}
                      </option>
                    ))}
                  </select>
                  <small className="doc_av_modal_calc_text">
                    Selected: {buildSlotSpanLabel(pendingSlotAfterClinic.time, pendingSlotAfterClinic.spanCount || 1, defaultSlotIntervalMinutes)}
                  </small>
                </div>
              </section>
            ) : null}

            <section className="doc_av_addclinic_section">
              <span className="doc_av_addclinic_kicker">Step 2</span>
              <label className="doc_av_addclinic_label" htmlFor="doc_av_clinic_address">
                Address
              </label>
              <textarea
                id="doc_av_clinic_address"
                className="doc_av_addclinic_textarea"
                rows={2}
                value={newClinicAddress}
                onChange={(e) => setNewClinicAddress(e.target.value)}
                placeholder="Full street address"
              />
              <div className="doc_av_addclinic_row3">
                <div className="doc_av_addclinic_field">
                  <label htmlFor="doc_av_clinic_city">City</label>
                  <input id="doc_av_clinic_city" className="doc_av_addclinic_input" type="text" value={newClinicCity} onChange={(e) => setNewClinicCity(e.target.value)} placeholder="City" />
                </div>
                <div className="doc_av_addclinic_field">
                  <label htmlFor="doc_av_clinic_state">State</label>
                  <input id="doc_av_clinic_state" className="doc_av_addclinic_input" type="text" value={newClinicState} onChange={(e) => setNewClinicState(e.target.value)} placeholder="State" />
                </div>
                <div className="doc_av_addclinic_field">
                  <label htmlFor="doc_av_clinic_pin">PIN</label>
                  <input id="doc_av_clinic_pin" className="doc_av_addclinic_input" type="text" value={newClinicPincode} onChange={(e) => setNewClinicPincode(e.target.value)} placeholder="Pincode" />
                </div>
              </div>
            </section>

            <details className="doc_av_addclinic_advanced">
              <summary>Map data (optional)</summary>
              <p className="doc_av_addclinic_hint">Usually filled automatically. Edit only if something looks wrong.</p>
              <label className="doc_av_addclinic_label" htmlFor="doc_av_place_id">
                Google Place ID
              </label>
              <input id="doc_av_place_id" className="doc_av_addclinic_input doc_av_addclinic_input--mono" type="text" value={newClinicPlaceId} onChange={(e) => setNewClinicPlaceId(e.target.value)} placeholder="ChIJ…" />
              <div className="doc_av_modal_grid2 doc_av_addclinic_latlng">
                <div className="doc_av_addclinic_field">
                  <label htmlFor="doc_av_lat">Latitude</label>
                  <input id="doc_av_lat" className="doc_av_addclinic_input doc_av_addclinic_input--mono" type="text" value={newClinicLat} onChange={(e) => setNewClinicLat(e.target.value)} placeholder="12.97…" />
                </div>
                <div className="doc_av_addclinic_field">
                  <label htmlFor="doc_av_lng">Longitude</label>
                  <input id="doc_av_lng" className="doc_av_addclinic_input doc_av_addclinic_input--mono" type="text" value={newClinicLng} onChange={(e) => setNewClinicLng(e.target.value)} placeholder="77.59…" />
                </div>
              </div>
            </details>
            </div>

            <div className="doc_av_modal_actions doc_av_addclinic_actions">
              <button type="button" className="doc_av_btn_secondary" onClick={closeAddClinicModal} disabled={saving}>
                Cancel
              </button>
              <button
                type="button"
                className="doc_av_btn_primary"
                onClick={() => void confirmAddClinic()}
                disabled={saving || String(newClinicNameInput || "").trim().length < 2}
              >
                {saving ? "Saving…" : editingClinicId ? "Save clinic" : pendingSlotAfterClinic ? "Add clinic + slot" : "Add clinic"}
              </button>
            </div>
          </div>
        </div>
      )}

      {typeof document !== "undefined" &&
        slotPeek &&
        slotPeekPosition &&
        createPortal(
          <div
            className="doc_av_slotpeek"
            style={{
              position: "fixed",
              zIndex: 6000,
              top: slotPeekPosition.top,
              left: slotPeekPosition.left,
              transform: slotPeekPosition.transform,
            }}
            role="dialog"
            aria-label="Slot details"
            onMouseEnter={keepPeekOpen}
            onMouseLeave={endCellPeekSoon}
          >
            <div className={`doc_av_slotpeek__pill doc_av_slotpeek__pill--${slotPeek.state}`}>
              {slotPeek.state === "available" ? "Available" : slotPeek.state === "booked" ? "Booked" : "Unavailable"}
            </div>
            <p className="doc_av_slotpeek__when">
              {dayLabelByDate[slotPeek.day] || slotPeek.day} · {formatDisplayTime(slotPeek.time)}
            </p>
            <p className="doc_av_slotpeek__clinic">{currentClinic?.name?.trim() || "Clinic"}</p>
            {slotPeek.state === "booked" && slotPeek.booked ? (
              <dl className="doc_av_slotpeek__dl">
                <div>
                  <dt>Patient</dt>
                  <dd>{slotPeek.booked.patientName}</dd>
                </div>
                {slotPeek.booked.patientMobile ? (
                  <div>
                    <dt>Mobile</dt>
                    <dd>{slotPeek.booked.patientMobile}</dd>
                  </div>
                ) : null}
                {slotPeek.booked.procedure ? (
                  <div>
                    <dt>Procedure</dt>
                    <dd>{slotPeek.booked.procedure}</dd>
                  </div>
                ) : null}
                <div>
                  <dt>Status</dt>
                  <dd>{slotPeek.booked.status}</dd>
                </div>
              </dl>
            ) : slotPeek.state === "booked" ? (
              <p className="doc_av_slotpeek__hint">Booked — click the slot for patient details.</p>
            ) : (
              <p className="doc_av_slotpeek__hint">
                {slotPeek.state === "available"
                  ? "Click the slot to edit or clear this time."
                  : "Click the slot to add availability for this clinic."}
              </p>
            )}
            {slotPeek.fromHold ? <p className="doc_av_slotpeek__dismiss">Tap outside this card to close.</p> : null}
          </div>,
          document.body
        )}
    </DoctorLayout>
  );
}
